import { toValue, type MaybeRefOrGetter } from "vue";
import { getTabId } from "~/utils/product/productTabIds";

const TAB_SELECTOR = "[role=\"tab\"]";
const ROVING_STOP = 0;
const OUT_OF_TAB_ORDER = -1;

interface TablistTab {
	value: string;
}

interface UseTablistKeyboardOptions {
	/** Tabs in render order — must match the DOM order of the `role="tab"` buttons. */
	tabs: MaybeRefOrGetter<readonly TablistTab[]>;
	/** Value of the currently selected tab. */
	selected: MaybeRefOrGetter<string>;
	/** Selects a tab. Activation is automatic: focus and selection move together. */
	onSelect: (value: string) => void;
}

/**
 * Resolves a WAI-ARIA Tabs keypress to the index that should receive focus.
 * Horizontal tablist: Left/Right wrap at the ends, Home/End jump. Returns
 * `null` for keys the pattern does not claim, so Tab/Enter/Space keep their
 * browser defaults.
 */
export const getNextTabIndex = (
	key: string,
	currentIndex: number,
	count: number
): number | null => {
	if (count === 0) return null;

	switch (key) {
		case "ArrowRight":
			return (currentIndex + 1) % count;
		case "ArrowLeft":
			return (currentIndex - 1 + count) % count;
		case "Home":
			return 0;
		case "End":
			return count - 1;
		default:
			return null;
	}
};

/**
 * Keyboard interaction model for a horizontal `role="tablist"` per the WAI-ARIA
 * Authoring Practices Tabs pattern.
 *
 * - Roving tabindex: the selected tab is the only `tabindex="0"` stop, so Tab
 *   enters and leaves the tablist in one keystroke.
 * - Left/Right move focus between tabs and wrap; Home/End jump to the ends.
 * - Activation follows focus, matching the existing click behaviour.
 *
 * Bind `handleKeydown` on the tablist element and `getTabindex(tab.value)` on
 * each tab button. Focus targets are read from the DOM under the event's
 * `currentTarget`, so templates keep full control over their markup.
 */
export const useTablistKeyboard = ({
	tabs,
	selected,
	onSelect
}: UseTablistKeyboardOptions) => {
	// The stop falls back to the first tab when the selection is not rendered
	// (e.g. a tab filtered out for lack of content); otherwise the tablist would
	// have no reachable tab at all.
	const getRovingStopValue = (): string | undefined => {
		const list = toValue(tabs);
		const current = toValue(selected);
		return list.some((tab) => tab.value === current) ? current : list[0]?.value;
	};

	const getTabindex = (value: string): number =>
		value === getRovingStopValue() ? ROVING_STOP : OUT_OF_TAB_ORDER;

	// Match the focused element back to its tab by id (`tab-<slug>`), so a
	// conditionally rendered button cannot shift the mapping. Falls back to DOM
	// order for markup that does not carry the id.
	const resolveTabValue = (
		element: HTMLElement | undefined,
		index: number
	): string | undefined => {
		const list = toValue(tabs);
		const byId = element?.id
			? list.find((tab) => getTabId(tab.value) === element.id)
			: undefined;
		return (byId ?? list[index])?.value;
	};

	const handleKeydown = (event: KeyboardEvent): void => {
		const tablist = event.currentTarget as HTMLElement | null;
		const target = event.target as HTMLElement | null;
		if (!tablist || !target) return;

		const tabElements = Array.from(
			tablist.querySelectorAll<HTMLElement>(TAB_SELECTOR)
		);
		const currentIndex = tabElements.findIndex(
			(element) => element === target || element.contains(target)
		);
		if (currentIndex === -1) return;

		const nextIndex = getNextTabIndex(
			event.key,
			currentIndex,
			tabElements.length
		);
		if (nextIndex === null) return;

		event.preventDefault();
		const nextElement = tabElements[nextIndex];
		nextElement?.focus();

		const nextValue = resolveTabValue(nextElement, nextIndex);
		if (nextValue !== undefined) onSelect(nextValue);
	};

	return { getTabindex, handleKeydown };
};
